<script lang="ts" setup>
import OverlayWithDialog from '@/ui/common/overlay/OverlayWithDialog.vue'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useI18n } from 'vue-i18n'
import {useEscapeKey} from "@/composables/use-escape-key";

const { t } = useI18n()

interface Props {
  isOpen: boolean
  threadTitle?: string
  isLoading?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'confirm'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const handleConfirm = () => {
  emit('confirm')
}

const handleClose = () => {
  emit('close')
}

useEscapeKey(handleClose, !props.isLoading)

</script>

<template>
  <OverlayWithDialog
      :is-open="isOpen"
      @close="handleClose"
  >
    <div class="confirm-delete-dialog">
      <TextView
          variant="roboto_18_bold"
          class="confirm-delete-dialog__title"
      >
        {{ t('dialogs.deleteThread.title') }}
      </TextView>

      <TextView
          variant="roboto_13_regular"
          class="confirm-delete-dialog__message"
      >
        {{ t('dialogs.deleteThread.message') }}
        <span v-if="threadTitle" class="confirm-delete-dialog__thread-title">
          "{{ threadTitle }}"
        </span>
        {{ t('dialogs.deleteThread.messageEnd') }}
      </TextView>

      <div class="confirm-delete-dialog__actions">
        <button
            class="confirm-delete-dialog__button confirm-delete-dialog__button--cancel"
            @click="handleClose"
            :disabled="isLoading"
        >
          <TextView variant="roboto_13_regular">
            {{ t('dialogs.deleteThread.cancel') }}
          </TextView>
        </button>

        <button
            class="confirm-delete-dialog__button confirm-delete-dialog__button--delete"
            @click="handleConfirm"
            :disabled="isLoading"
        >
          <TextView variant="roboto_13_regular">
            {{ isLoading ? t('dialogs.deleteThread.deleting') : t('dialogs.deleteThread.delete') }}
          </TextView>
        </button>
      </div>
    </div>
  </OverlayWithDialog>
</template>

<style lang="scss" scoped>
.confirm-delete-dialog {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  &__title {
    color: var(--gray_90);
    text-align: center;
  }

  &__message {
    color: var(--gray_70);
    text-align: center;
    line-height: 1.5;
  }

  &__thread-title {
    color: var(--gray_90);
    font-weight: 500;
  }

  &__actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
  }

  &__button {
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-width: 80px;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--cancel {
      background: var(--gray_10);

      &:hover:not(:disabled) {
        background: var(--gray_20);
      }
    }

    &--delete {
      background: var(--error_50);
      color: var(--white);

      &:hover:not(:disabled) {
        background: var(--error_60);
      }
    }
  }
}
</style>